import * as pdfjsLib from "pdfjs-dist";

let workerConfigured = false;

/** Same worker setup as context-signature (known working in this app). */
export function ensurePdfJsWorkerForViewer(): void {
  if (workerConfigured) {
    return;
  }
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
  workerConfigured = true;
}

function isPdfBlob(blob: Blob): boolean {
  const type = (blob.type || "").toLowerCase();
  return !type || type === "application/pdf" || type === "application/octet-stream";
}

export async function renderPdfBlobIntoHost(
  host: HTMLElement,
  blob: Blob,
  title = "PDF preview"
): Promise<void> {
  ensurePdfJsWorkerForViewer();
  host.replaceChildren();

  if (!blob.size) {
    throw new Error("Empty PDF blob");
  }

  const bytes = new Uint8Array(await blob.arrayBuffer());
  if (bytes.length >= 4) {
    const magic = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
    if (magic !== "%PDF" && !isPdfBlob(blob)) {
      throw new Error("Not a PDF file");
    }
  }

  const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
  const hostWidth = Math.max(host.clientWidth - 24, 320);

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(
      1.75,
      Math.max(0.65, hostWidth / baseViewport.width)
    );
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.className = "light-pdf-preview__page";
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.setAttribute("role", "img");
    canvas.setAttribute(
      "aria-label",
      `${title} page ${pageNum} of ${pdf.numPages}`
    );
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas not available");
    }
    await page.render({ canvasContext: ctx, viewport }).promise;
    host.appendChild(canvas);
  }
}
